import {
  ChangeDetectorRef, Component, Inject, OnInit, OnDestroy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { map} from 'rxjs/operators';

import { isNullOrUndefined } from '@app/util/util';
import { IS_BROWSER, IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { AppFacade } from '@app/app.facade';
import { FlightType } from '@search-bar/entities/flight-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SearchType } from '@search-bar/entities/search-type';
import {
  SearchBarCalendarAbstractComponent
} from '@shared/base-search-bar/components/calendar/abstract/search-bar-calendar.abstract';
import { TranslocoService } from '@jsverse/transloco';
import { MinMaxCalendarInterface } from '@search-bar/mappers/exchange-url.mapper';
import { TripTypes } from '@app/entities/trip-types';
import { isByHours } from '@hotels/hotel-search/componenets/byhours-search/mappers/byhours.mapper';
import { allowSameDaySelection, daysOrNightsNumberLabel } from '@shared/base-search-bar/mappers/search-bar-calendar.mapper';
import {
  DateCalendarDefaultDates, DateCalendarField, DateCalendarInputBooleanValues, DateCalendarSelectedEvent, DateCalendarType
} from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';
import { DropDownViewCloseType, DropDownViewSlide } from '@ux/components/drop-down-view/entities/drop-down-view.types';

@Component({
  selector: 'app-search-bar-mobile-calendar',
  templateUrl: './search-bar-mobile-calendar.container.html',
  styleUrls: ['./search-bar-mobile-calendar.container.sass'],
  standalone: false
})
export class SearchBarMobileCalendarContainer extends SearchBarCalendarAbstractComponent implements OnInit, OnDestroy {

  public animated = true;

  public activeType: CalendarField;
  public departurePlaceholder: string;
  public returnPlaceholder: string;
  public searchType: SearchType;
  public headerSticky: boolean;
  public departureSelectionDisabled: boolean;
  public exchangeTripType: TripTypes;
  public isExchangeCalendar: boolean = false;

  public daysBetweenTwoDates = 0;

  private minMaxCalendarInterface: MinMaxCalendarInterface;

  public readonly dateCalendarType = DateCalendarType.FullPage;
  public readonly timeToSlide = TIME_TO_SLIDE;

  public readonly closeType = DropDownViewCloseType.Route;
  public readonly slide = DropDownViewSlide.Top;

  constructor(
    protected facade: AppFacade,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly baseSearchBarFacade: BaseSearchBarFacade,
    protected translateService: TranslocoService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(IS_MOBILE) public isMobile: boolean,
    @Inject(IS_BROWSER) public isBrowser: boolean) {
    super(changeDetectorRef, facade, translateService);
    const state = this.router.getCurrentNavigation();
    if (state && state?.extras && state?.extras?.state) {
      const {
        activeType, searchType, returnPlaceholder, departurePlaceholder, isExchangeCalendar,
        departureSelectionDisabled, minMaxCalendarInterface, exchangeTripType,
      } = this.router.getCurrentNavigation()?.extras?.state;
      this.activeType = activeType;
      this.searchType = searchType;
      this.returnPlaceholder = this.translateService.translate(returnPlaceholder);
      this.departurePlaceholder = this.translateService.translate(departurePlaceholder);
      this.departureSelectionDisabled = !!departureSelectionDisabled;
      this.minMaxCalendarInterface = minMaxCalendarInterface;
      this.exchangeTripType = exchangeTripType;
      this.isExchangeCalendar = !!isExchangeCalendar;
    } else {
      this.route.queryParamMap.subscribe(params => {
        if (params && params.get('validateWithParams') && params.get('validateWithParams').toLowerCase() === 'true') {
          this.activeType = params.get('activeType') as CalendarField;
          this.searchType = params.get('searchType') as SearchType;
          this.returnPlaceholder = this.translateService.translate(params.get('returnPlaceholder'));
          this.departurePlaceholder = this.translateService.translate(params.get('departurePlaceholder'));
          this.departureSelectionDisabled = !!params.get('departureSelectionDisabled') &&
            params.get('departureSelectionDisabled').toLowerCase() === 'true';
          this.minMaxCalendarInterface = params.get('minMaxCalendarInterface') ?
            JSON.parse(params.get('minMaxCalendarInterface')) as MinMaxCalendarInterface : undefined;
          this.exchangeTripType = params.get('exchangeTripType') ?
            Number(params.get('exchangeTripType')) as TripTypes : undefined;
        }
      }).unsubscribe();
    }
  }

  ngOnInit(): void {
    this._activeType = this.activeType;
    this.returnPlaceholder = isNullOrUndefined(this.returnPlaceholder) ?
      this.translateService.translate('SEARCH_MODULE.LOCATION_SEARCH.RETURN') : this.returnPlaceholder;
    this.departurePlaceholder = isNullOrUndefined(this.departurePlaceholder) ?
      this.translateService.translate('SEARCH_MODULE.LOCATION_SEARCH.DEPART') : this.departurePlaceholder;
    this.subscriptions.push(
      this.facade.user$.subscribe(user => {
        this.user = user;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get departureDate$(): Observable<Date> {
    return this.baseSearchBarFacade.departureDate$;
  }

  get arrivalDate$(): Observable<Date> {
    return this.baseSearchBarFacade.arrivalDate$;
  }

  get flightType$(): Observable<FlightType> {
    return this.baseSearchBarFacade.flightType$;
  }

  get isOneWayTrip$(): Observable<boolean> {
    return this.flightType$.pipe(map(flightType => {
      const isOneWayFlightType = flightType === FlightType.ONEWAY;
      const isFlightOrBusSearch =
        this.searchType === SearchType.FLIGHT_RESULTS ||
        this.searchType === SearchType.HOME_FLIGHT ||
        this.searchType === SearchType.HOME_BUSES;
      return isByHours(this.searchType) || (isOneWayFlightType && isFlightOrBusSearch);
    }));
  }

  get dateCalendarEnabledInputs$(): Observable<DateCalendarInputBooleanValues> {
    return this.isOneWayTrip$.pipe(map((isOneWayTrip) => {
      const left = this.exchangeTripType ? this.exchangeTripType === TripTypes.DEPARTURE : true;
      const right = this.exchangeTripType ? 
        ((isOneWayTrip && this.exchangeTripType === TripTypes.RETURN) || !isOneWayTrip) : !isByHours(this.searchType);
      return { left, right };
    }))
  }

  get hasCalendarInterface(): boolean {
    return !!this.minMaxCalendarInterface;
  }

  get hasMaxCalendarInterface(): boolean {
    return this.hasCalendarInterface && !!this.minMaxCalendarInterface.MAX;
  }

  get hasMinCalendarInterface(): boolean {
    return this.hasCalendarInterface && !!this.minMaxCalendarInterface.MIN;
  }

  get daysBetweenTwoDatesLabel(): string {
    return this.daysBetweenTwoDatesLabelAbstract(this.daysBetweenTwoDates, this.searchType);
  }

  get allowTheSameDay(): boolean {
    return allowSameDaySelection(this.searchType);
  }

  get fieldActivated(): DateCalendarField {
    return this._activeType === CalendarField.departure ? 'left' : 'right';
  }

  get defaultDates$(): Observable<DateCalendarDefaultDates> {
    return combineLatest([this.departureDate$, this.arrivalDate$])
      .pipe(map(([departureDate, arrivalDate]) => [departureDate ?? null, arrivalDate ?? null]))
  }

  get minDate(): number {
    if (this.hasMinCalendarInterface) {
      return this.minMaxCalendarInterface?.MIN?.getTime() ?? null;
    } else {
      const disabledDaysByUserConfig = this.calendarDaysLimit(
        this.searchType, this.user?.id, this.user?.is_prime,
        this.isDiamondUser(this.user), this.user?.has_rappicard,
      );
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      return currentDate.setDate(currentDate.getDate() + disabledDaysByUserConfig);
    }
  }

  get maxDate(): number {
    if (this.hasMaxCalendarInterface) {
      return this.minMaxCalendarInterface?.MAX?.getTime() ?? null;
    } else {
      return null;
    }
  }

  get isValidDateCalendar$(): Observable<boolean> {
    return combineLatest([this.isOneWayTrip$, this.departureDate$, this.arrivalDate$])
      .pipe(
        map(([isOneWayTrip, departureDate, arrivalDate]) => {
          if (this.isExchangeCalendar) {

          } else {
            return isOneWayTrip ? !isNullOrUndefined(departureDate) :
              !isNullOrUndefined(departureDate) && !isNullOrUndefined(arrivalDate);
          }
        })
      );
  }

  public selectEvent(dates: DateCalendarSelectedEvent): void {
    this.daysBetweenTwoDates = daysOrNightsNumberLabel(this.searchType, dates?.days, dates?.nights);
    this.baseSearchBarFacade.selectCalendarDates([dates?.left?.date ?? null, dates?.right?.date ?? null]);
    this.changeDetectorRef.detectChanges();
  }

  public close(): void {
    this.daysBetweenTwoDates = 0;
    this.animated = false;
  }

  public selectFlightType(event: FlightType): void {
    this.baseSearchBarFacade.selectFlightType(event.toString() as FlightType);
  }
}
